import type {FormInstance} from 'antd';

import type {
  A3ManufacturingValues,
  FacilityRow,
  ProductionDataValues,
  ProductionSiteRow
} from '../../../services/interface/pcfTypes';

import {getProductionFromForm} from './lciProductionUtils';

const a3Defaults: A3ManufacturingValues = {
  facilities: [],
  activeFacility: null
};

const blankEnergy = (zone = 'tamil_nadu') => ({
  type: 'grid_electricity',
  quantity: '',
  unit: 'kwh',
  zone,
  ef: '',
  rec: false,
  recQty: ''
});

const blankProcess = () => ({
  type: 'co2_fossil_process',
  qty: '',
  gwp: '1'
});

const blankWater = () => ({
  consumption: '',
  region: 'indus_basin'
});

const blankWaste = (type = 'solid_non_hazardous') => ({
  type,
  qty: '',
  treatment: 'landfilled',
  wasteScope: 'whole_facility_units',
  ef: ''
});

export const blankFacilityFromSite = (
  site: ProductionSiteRow,
  allocationBasis: string,
  configured = false
): FacilityRow => ({
  name: site.name,
  state: site.state,
  allocationBasis: allocationBasis || 'units_produced',
  facilityThroughput: site.facilityThroughput ?? '',
  configured,
  hasProcessEmissions: 'no',
  processAllocationScope: '',
  wasteTypesSelected: [],
  noWasteJustification: '',
  energy: [blankEnergy(site.state || 'tamil_nadu')],
  process: [blankProcess()],
  water: [blankWater()],
  waste: [blankWaste()]
});

const blankSingleSiteFacility = (
  production: ProductionDataValues
): FacilityRow => ({
  name: 'Primary manufacturing site',
  state: 'tamil_nadu',
  allocationBasis: production.allocationBasis || 'units_produced',
  facilityThroughput: '',
  configured: true,
  hasProcessEmissions: 'no',
  processAllocationScope: '',
  wasteTypesSelected: [],
  noWasteJustification: '',
  energy: [blankEnergy()],
  process: [blankProcess()],
  water: [blankWater()],
  waste: [blankWaste()]
});

const inferConfiguredFromLegacyData = (facility: FacilityRow): boolean => {
  if (facility.configured === true) {
    return true;
  }

  if (facility.configured === false) {
    return false;
  }

  const hasEnergyData = facility.energy?.some(
    (row) => row.quantity != null && String(row.quantity).trim() !== ''
  );
  const hasProcessData =
    facility.hasProcessEmissions === 'yes' ||
    facility.process?.some(
      (row) => row.qty != null && String(row.qty).trim() !== ''
    );
  const hasWaterData = facility.water?.some(
    (row) =>
      row.consumption != null && String(row.consumption).trim() !== ''
  );
  const hasWasteData =
    (facility.wasteTypesSelected?.length ?? 0) > 0 ||
    Boolean(facility.noWasteJustification?.trim()) ||
    facility.waste?.some(
      (row) => row.qty != null && String(row.qty).trim() !== ''
    );

  return Boolean(hasEnergyData || hasProcessData || hasWaterData || hasWasteData);
};

export const buildA3FacilitiesFromProduction = (
  production: ProductionDataValues | undefined
): FacilityRow[] => {
  if (!production) {
    return [];
  }

  const setup = production.setup ?? 'single_site';

  if (setup === 'single_site') {
    return [blankSingleSiteFacility(production)];
  }

  if (!production.sites?.length) {
    return [];
  }

  return production.sites.map((site) =>
    blankFacilityFromSite(site, production.allocationBasis, false)
  );
};

export const resetA3FacilityAtIndex = (
  form: FormInstance,
  index: number,
  production: ProductionDataValues
): void => {
  const site = production.sites?.[index];

  if (!site) {
    return;
  }

  const resetRow = blankFacilityFromSite(site, production.allocationBasis, false);

  form.setFieldValue(['a3', 'facilities', index], resetRow);
};

const mergeA3FacilityRows = (
  nextFacilities: FacilityRow[],
  currentFacilities: FacilityRow[],
  isMultiSite: boolean
): FacilityRow[] =>
  nextFacilities.map((facility, index) => {
    const existing = currentFacilities[index];
    const existingByName = currentFacilities.find(
      (row) => row.name?.trim() && row.name === facility.name
    );
    const matched = existing?.name === facility.name ? existing : existingByName;

    if (!matched) {
      return facility;
    }

    const configured = isMultiSite
      ? inferConfiguredFromLegacyData(matched)
      : true;

    return {
      ...facility,
      ...matched,
      name: facility.name?.trim() ? facility.name : matched.name,
      state: facility.state,
      allocationBasis: facility.allocationBasis,
      facilityThroughput: facility.facilityThroughput,
      configured
    };
  });

const facilitiesMetadataChanged = (
  currentFacilities: FacilityRow[],
  mergedFacilities: FacilityRow[]
): boolean => {
  if (currentFacilities.length !== mergedFacilities.length) {
    return true;
  }

  return mergedFacilities.some(
    (facility, index) =>
      facility.name !== currentFacilities[index]?.name ||
      facility.state !== currentFacilities[index]?.state ||
      facility.allocationBasis !== currentFacilities[index]?.allocationBasis ||
      String(facility.facilityThroughput ?? '') !==
        String(currentFacilities[index]?.facilityThroughput ?? '') ||
      facility.configured !== currentFacilities[index]?.configured
  );
};

const facilitiesNeedSync = (
  currentFacilities: FacilityRow[],
  nextFacilities: FacilityRow[]
): boolean => {
  if (currentFacilities.length !== nextFacilities.length) {
    return true;
  }

  const currentNames = currentFacilities.map((facility) => facility.name).join('|');
  const nextNames = nextFacilities.map((facility) => facility.name).join('|');

  return currentNames !== nextNames;
};

export const syncA3FacilitiesFromProduction = (
  form: FormInstance,
  productionFallback?: ProductionDataValues
): boolean => {
  const production = getProductionFromForm(form, productionFallback);
  const nextFacilities = buildA3FacilitiesFromProduction(production);

  if (!nextFacilities.length) {
    return false;
  }

  const isMultiSite =
    production?.setup === 'multiple_sites' ||
    production?.setup === 'contract_manufacturer';

  const currentA3 =
    (form.getFieldValue('a3') as A3ManufacturingValues | undefined) ?? a3Defaults;
  const currentFacilities = currentA3.facilities ?? [];

  if (!facilitiesNeedSync(currentFacilities, nextFacilities)) {
    const mergedFacilities = mergeA3FacilityRows(
      nextFacilities,
      currentFacilities,
      isMultiSite
    );
    const activeFacility = currentA3.activeFacility;
    const activeIsValid =
      activeFacility != null &&
      activeFacility >= 0 &&
      activeFacility < nextFacilities.length;

    if (facilitiesMetadataChanged(currentFacilities, mergedFacilities)) {
      form.setFieldsValue({
        a3: {
          ...currentA3,
          facilities: mergedFacilities,
          activeFacility: activeIsValid ? activeFacility : 0
        }
      });

      return true;
    }

    if (!activeIsValid && nextFacilities.length > 0) {
      form.setFieldsValue({
        a3: {
          ...currentA3,
          activeFacility: 0
        }
      });

      return true;
    }

    return false;
  }

  form.setFieldsValue({
    a3: {
      ...currentA3,
      facilities: mergeA3FacilityRows(
        nextFacilities,
        currentFacilities,
        isMultiSite
      ),
      activeFacility: currentA3.activeFacility ?? 0
    }
  });

  return true;
};
